import {
  AutocompleteApi,
  AutocompleteState,
  createAutocomplete
} from '@algolia/autocomplete-core'
import { useMediaQuery } from '@mui/material'
import { DateRange } from '@mui/x-date-pickers-pro'
import { bbox } from '@turf/bbox'
import { buffer } from '@turf/buffer'
import { center } from '@turf/center'
import { point, points } from '@turf/helpers'
import { endOfDay, format, Locale, startOfDay } from 'date-fns'
import { isNonEmptyArray } from 'effect/Array'
import { TFunction } from 'i18next'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { match } from 'ts-pattern'
import { LocaleContext } from 'components/common/LocaleContext'
import {
  defaultSortBy,
  searchClient,
  searchIndex
} from 'containers/meilisearchClient'
import defaultStartDate from 'domain/constants/EditionLiveStartDateTimestamp'
import { FRANCE_GEO_SEARCH } from 'domain/constants/FranceGeoCoordinates'
import { raceCategoriesLevel1 } from 'helpers/raceCategoryLevels'
import { themeUtils } from 'styles/theme'
import { Option } from './LocationTabContent'
import { EventCategory, EventType, TypeTab } from './TypeTabContent'

const filters = `editionLiveEndDateTimestamp>${defaultStartDate} AND eventStatus="LIVE"`

const attributesToRetrieve = [
  'eventName',
  'eventSlug',
  'eventIsFeatured',
  'objectID',
  'eventCountrySubdivisionDisplayCodeLevel2',
  'eventCity',
  'editionLiveStartDateTimestamp',
  'editionLiveEndDateTimestamp',
  'editionCalendarStatus'
]

const locationAttributesToRetrieve = [
  'name',
  'id',
  '_geo',
  'admin2 code',
  'feature code',
  'bbox'
]

const formatDateRange = (
  date: DateRange<Date>,
  t: TFunction<'common'>,
  locale: Locale
) => {
  const [start, end] = date
  if (!start) return ''

  if (!end || startOfDay(start).getTime() === startOfDay(end).getTime()) {
    const dayMonth = format(start, 'd MMMM', { locale })
    return dayMonth
  }

  const sameMonth =
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  const endStr = format(end, 'd MMMM', { locale })

  const startStr = sameMonth
    ? format(start, 'd', { locale })
    : format(start, 'd MMMM', { locale })

  return t('header.rangeDate', { startDate: startStr, endDate: endStr })
}

const getDefaultDateRange = (
  from: string | undefined,
  to: string | undefined
): DateRange<Date> => {
  if (!from && !to) {
    return [null, null]
  }
  const fromDate = from ? new Date(Number(from) * 1000) : null
  const toDate = to ? new Date(Number(to) * 1000) : null
  return [fromDate, toDate]
}

const getDefaultRaceType = (
  editionLiveLevel2Category: string | string[] | undefined,
  t: TFunction<'common'>
): RaceType | null => {
  if (!editionLiveLevel2Category) {
    return null
  }
  const values = [editionLiveLevel2Category].flat()
  if (values.length === 1) {
    return {
      facet: 'editionLiveLevel2Category',
      key: values[0]!,
      label: t(values[0] as never)
    }
  }

  const level1Category = Object.entries(raceCategoriesLevel1).find(
    ([_, { level2Categories }]) => {
      return (
        values.length === level2Categories.length &&
        level2Categories.every((category) => values.includes(category))
      )
    }
  )
  if (level1Category) {
    return {
      facet: 'editionLiveLevel1Category',
      key: level1Category[0]!,
      label: t(level1Category[0] as never)
    }
  }
  return null
}

const isFourNumbersSplittedString = (
  str: unknown[]
): str is [number, number, number, number] => {
  return str.length === 4 && str.every((item) => typeof item === 'number')
}

const getDefaultLocation = (
  geoLocation: string | undefined,
  geoSearch: string | undefined
): Option | null => {
  if (!geoLocation || !geoSearch) {
    return null
  }

  const values = geoSearch.split(',').map(Number)
  if (isFourNumbersSplittedString(values)) {
    const [maxLat, maxLng, minLat, minLng] = values
    const point = center(
      points([
        [maxLng, maxLat],
        [minLng, minLat]
      ])
    )
    return {
      id: geoLocation,
      name: geoLocation,
      _geo: {
        lat: point.geometry.coordinates[1]!,
        lng: point.geometry.coordinates[0]!
      }
    }
  }
  return null
}

interface RaceType extends EventCategory {
  facet:
    | 'editionLiveLevel2Category'
    | 'editionLiveLevel1Category'
    | 'freeSearch'
}

interface SearchBarContext {
  locationSearch: string
  setLocationSearch: Dispatch<SetStateAction<string>>
  locationAutocompleteState: AutocompleteState<Option> | null | undefined
  dateRange: DateRange<Date>
  setDateRange: Dispatch<SetStateAction<DateRange<Date>>>
  formattedDateRange?: string
  location: Option | null | undefined
  setLocation: Dispatch<SetStateAction<Option | null | undefined>>
  raceSearch: string
  setRaceSearch: Dispatch<SetStateAction<string>>
  raceAutocompleteState: AutocompleteState<TypeTab> | null | undefined
  raceType: RaceType | null
  setRaceType: Dispatch<SetStateAction<RaceType | null>>
  submitSearch: () => void
  typeAutocomplete: AutocompleteApi<TypeTab>
  locationAutocomplete: AutocompleteApi<Option>
}

export const SearchBarContext = createContext<SearchBarContext>({
  locationSearch: '',
  setLocationSearch: () => {},
  locationAutocompleteState: null,
  dateRange: [null, null],
  setDateRange: () => {},
  location: null,
  setLocation: () => {},
  raceSearch: '',
  setRaceSearch: () => {},
  raceAutocompleteState: null,
  raceType: null,
  setRaceType: () => {},
  submitSearch: () => {},
  typeAutocomplete: {} as AutocompleteApi<TypeTab>,
  locationAutocomplete: {} as AutocompleteApi<Option>
})

export const SearchBarProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation('common')
  const isMobile = useMediaQuery(themeUtils.maxWidth.md)

  const { fnsLocale } = useContext(LocaleContext)
  const { push, query, isReady } = useRouter()
  const {
    editionLiveLevel2Category,
    fromTimestamp,
    toTimestamp,
    geoSearch,
    geoLocation
  } = query

  const [locationSearch, setLocationSearch] = useState<string>('')
  const [locationAutocompleteState, setLocationAutocompleteState] =
    useState<AutocompleteState<Option> | null>()
  const [dateRange, setDateRange] = useState<DateRange<Date>>([null, null])
  const [location, setLocation] = useState<Option | null>()
  const [raceSearch, setRaceSearch] = useState<string>('')
  const [raceAutocompleteState, setRaceAutocompleteState] =
    useState<AutocompleteState<TypeTab> | null>()
  const [raceType, setRaceType] = useState<RaceType | null>(null)
  const typeAutocomplete = useMemo(
    () =>
      createAutocomplete<TypeTab>({
        openOnFocus: true,
        getSources: ({ query }) => [
          {
            sourceId: 'suggestedEvents',
            getItems: async () => {
              const { results } = await searchClient.search<EventType>([
                {
                  indexName: defaultSortBy,
                  params: {
                    query,
                    filters: 'isRecommended=true',
                    attributesToRetrieve,
                    attributesToHighlight: ['eventName']
                  }
                }
              ])
              if (
                isNonEmptyArray(results) &&
                'hits' in results[0] &&
                results[0].hits &&
                results[0].hits.length > 0
              ) {
                return results[0].hits
              }
              return []
            }
          },
          {
            sourceId: 'events',
            getItems: async () => {
              // Utilisation de la recherche par défaut (sans tri explicite) pour l'autocomplete
              const { results } = await searchClient.search<EventType>([
                {
                  indexName: searchIndex,
                  params: {
                    query,
                    filters,
                    hitsPerPage: isMobile ? 3 : 5,
                    maxValuesPerFacet: 40,
                    attributesToRetrieve,
                    attributesToHighlight: ['eventName'],
                    restrictSearchableAttributes: ['eventName'],
                    highlightPreTag: '<strong>',
                    highlightPostTag: '</strong>'
                  }
                }
              ])
              if (
                isNonEmptyArray(results) &&
                'hits' in results[0] &&
                results[0].hits &&
                results[0].hits.length > 0
              ) {
                return results[0].hits
              }
              return []
            }
          },
          {
            sourceId: 'editionCategories',
            getItems: async () => {
              const { results } = await searchClient.search<EventType>([
                {
                  indexName: searchIndex,
                  params: {
                    query,
                    filters,
                    facets: [
                      'editionLiveLevel1Category',
                      'editionLiveLevel2Category'
                    ],
                    maxValuesPerFacet: 40,
                    // FIXME: ensure that this removal will not break anything
                    // maxFacetHits: 4,
                    hitsPerPage: 0,
                    attributesToRetrieve: [],
                    attributesToHighlight: []
                  }
                }
              ])
              if (
                isNonEmptyArray(results) &&
                'facets' in results[0] &&
                results[0].facets &&
                (results[0].facets['editionLiveLevel2Category'] ||
                  results[0].facets['editionLiveLevel1Category'])
              ) {
                return [
                  {
                    ...results[0].facets['editionLiveLevel2Category'],
                    ...results[0].facets['editionLiveLevel1Category']
                  }
                ].flatMap((category) => {
                  return Object.keys(category).map((key) => ({
                    key: key.split('||')[0]!,
                    label: key.split('||')[1]!
                  }))
                })
              }
              return []
            }
          }
        ],
        onStateChange({ state }) {
          setRaceAutocompleteState(state)
        }
      }),
    []
  )
  const locationAutocomplete = useMemo(
    () =>
      createAutocomplete<Option>({
        openOnFocus: true,
        getSources: ({ query }) => [
          {
            sourceId: 'suggestedLocations',
            getItems: async () => {
              const { results } = await searchClient.search<Option>([
                {
                  indexName: 'geonames',
                  params: {
                    query,
                    filters: 'isRecommended=true',
                    attributesToRetrieve: locationAttributesToRetrieve,
                    attributesToHighlight: ['name']
                  }
                }
              ])
              return results.map((result) => {
                if ('hits' in result) {
                  return result.hits
                }
                return []
              })
            }
          },
          {
            sourceId: 'departments',
            getItems: async () => {
              const { results } = await searchClient.search<Option>([
                {
                  indexName: 'geonames',
                  params: {
                    hitsPerPage: 100,
                    filters: '"feature code"="ADM2"',
                    attributesToRetrieve: ['admin2 code', 'name', 'id'],
                    attributesToHighlight: ['name']
                  }
                }
              ])
              return results.map((result) => {
                if ('hits' in result) {
                  return result.hits
                }
                return []
              })
            }
          },
          {
            sourceId: 'locations',
            getItems: async () => {
              const { results } = await searchClient.search<Option>([
                {
                  indexName: 'geonames',
                  params: {
                    query,
                    hitsPerPage: 5,
                    filters:
                      '"feature code"="PPL" OR "feature code"="PPLA2" OR "feature code"="PPLA" OR "feature code"="PPLA3" OR "feature code"="PPLC" OR "feature code"="ADM1" OR "feature code"="ADM2"',
                    attributesToRetrieve: locationAttributesToRetrieve,
                    attributesToHighlight: ['name'],
                    highlightPreTag: '<strong>',
                    highlightPostTag: '</strong>'
                  }
                }
              ])
              return results.map((result) => {
                if ('hits' in result) {
                  return result.hits
                }
                return []
              })
            }
          }
        ],
        onStateChange({ state }) {
          setLocationAutocompleteState(state)
        }
      }),
    []
  )

  useEffect(() => {
    if (isReady) {
      setDateRange(
        getDefaultDateRange(
          fromTimestamp as string | undefined,
          toTimestamp as string | undefined
        )
      )
      setRaceType(getDefaultRaceType(editionLiveLevel2Category, t))
      setLocation(
        getDefaultLocation(
          geoLocation as string | undefined,
          geoSearch as string | undefined
        )
      )
    }
  }, [isReady])

  const submitSearch = () => {
    const query: Record<string, string | readonly string[]> = {
      q: ''
    }
    if (location) {
      // bbox should be defined for ADM1 and ADM2 locations (departments, regions)
      if (location.bbox) {
        query.geoSearch = [location.bbox.northEast, location.bbox.southWest]
          .flat()
          .join()
      } else {
        const center = point([location._geo.lng, location._geo.lat])
        const buffered = buffer(center, 30, { units: 'kilometers' })
        if (buffered) {
          const [minLng, minLat, maxLng, maxLat] = bbox(buffered)
          const southWest = [minLat, minLng]
          const northEast = [maxLat, maxLng]

          query.geoSearch = [northEast, southWest].flat().join()
        }
      }
      query.geoLocation = location.name
    } else {
      // Use France as default location if no location is selected
      query.geoSearch = FRANCE_GEO_SEARCH
      query.geoLocation = 'France'
    }
    if (dateRange) {
      if (dateRange[0]) {
        query.fromTimestamp = Math.floor(
          dateRange[0].getTime() / 1000
        ).toString()
      }
      if (dateRange[1]) {
        query.toTimestamp = Math.floor(dateRange[1].getTime() / 1000).toString()
      } else if (dateRange[0]) {
        query.toTimestamp = Math.floor(
          endOfDay(dateRange[0]).getTime() / 1000
        ).toString()
      }
    }

    if (raceType) {
      match(raceType)
        .with({ facet: 'freeSearch' }, () => {
          query.q = raceType.label
        })
        .with({ facet: 'editionLiveLevel1Category' }, () => {
          const { level2Categories } =
            raceCategoriesLevel1[
              raceType.key as keyof typeof raceCategoriesLevel1
            ]

          query.editionLiveLevel2Category = level2Categories
        })
        .with({ facet: 'editionLiveLevel2Category' }, () => {
          query.editionLiveLevel2Category = raceType.key
        })
        .exhaustive()
    }

    push({ pathname: '/search', query })
  }

  const value = {
    locationSearch,
    setLocationSearch,
    locationAutocompleteState,
    dateRange,
    setDateRange,
    formattedDateRange: formatDateRange(dateRange, t, fnsLocale),
    location,
    setLocation,
    raceSearch,
    setRaceSearch,
    raceAutocompleteState,
    raceType,
    setRaceType,
    submitSearch,
    typeAutocomplete,
    locationAutocomplete
  }
  return (
    <SearchBarContext.Provider value={value}>
      {children}
    </SearchBarContext.Provider>
  )
}
